import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "scroller" }
const _hoisted_2 = { class: "scroller-box" }
const _hoisted_3 = ["src", "alt", "onClick"]
const _hoisted_4 = { class: "scroller-component-div" }
const _hoisted_5 = { class: "scroller-arrow-container" }

import { ref, computed, onMounted, onUnmounted, defineComponent, watch } from 'vue';
import { useImageViewer } from '../composables/useImageViewer';
import CardArrow from '../global/CardArrow.vue';

interface PhotoBoxImage {
  src: string;
  title?: string;
}

interface Props {
  photoBoxes?: Record<string, PhotoBoxImage[]>;
  componentBoxes?: Array<ReturnType<typeof defineComponent>>
}
const maxImageWidth = 360;
const maxImageHeight = 500;
const mobileBreakpoint = 1024;

export default /*@__PURE__*/_defineComponent({
  __name: 'HorizontalScroll',
  props: {
    photoBoxes: {},
    componentBoxes: {}
  },
  setup(__props: any) {

const props = __props;

const { openImageMenu, imageViewIsOpen } = useImageViewer();

const currentIndex = ref<number>(0);
const imageSizes = ref<Record<string, { width: number; height: number }>>({});
const viewportWidth = ref(window.innerWidth);
const isMobileLayout = computed(() => viewportWidth.value < mobileBreakpoint);
const displayPhotoBoxes = computed<Record<string, PhotoBoxImage[]>>(() => {
  if (!props.photoBoxes) {
    return {};
  }

  if (!isMobileLayout.value) {
    return props.photoBoxes;
  }

  return Object.entries(props.photoBoxes).reduce<Record<string, PhotoBoxImage[]>>((accumulator, [boxName, images]) => {
    images.forEach((image, index) => {
      accumulator[`${boxName} ${index + 1}`] = [image];
    });
    return accumulator;
  }, {});
});
const totalItems = computed(() => {
  if (Object.keys(displayPhotoBoxes.value).length > 0) {
    return Object.keys(displayPhotoBoxes.value).length;
  } else if (props.componentBoxes) {
    return props.componentBoxes.length;
  }
  return 0;
});
let interval: ReturnType<typeof setInterval> | null = null;

const isCenterImage = (images: PhotoBoxImage[], index: number) => images.length > 1 && index === Math.floor(images.length / 2);

const isSideImage = (images: PhotoBoxImage[], index: number) => images.length > 1 && !isCenterImage(images, index);

const getScaledImageSize = (src: string) => {
  const naturalSize = imageSizes.value[src];
  const targetWidth = maxImageWidth;
  const targetHeight = maxImageHeight;

  if (!naturalSize) {
    return { width: targetWidth, height: targetHeight };
  }

  const width = naturalSize.width;
  const height = naturalSize.height;
  const scale = Math.min(targetWidth / width, targetHeight / height, 1);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const getImageStyle = (src: string) => {
  const size = getScaledImageSize(src);

  return {
    width: `${size.width}px`,
    height: `${size.height}px`,
    maxWidth: '100%',
    maxHeight: '100%',
    display: 'block',
    objectFit: 'contain' as const,
    borderRadius: '20px',
  } as const;
};

const handleImageClick = (src: string, title: string, event: MouseEvent) => {
  openImageMenu(src, title, event.currentTarget);
};

const loadImageDimensions = () => {
  const imageSources = props.photoBoxes ? Object.values(props.photoBoxes).flat().map((image) => image.src) : [];

  imageSources.forEach((src) => {
    if (imageSizes.value[src] || !src) {
      return;
    }

    const img = new Image();
    img.src = src;
    img.onload = () => {
      imageSizes.value[src] = {
        width: img.naturalWidth,
        height: img.naturalHeight,
      };
    };
  });
};

watch(
  () => props.photoBoxes,
  () => {
    loadImageDimensions();
  },
  { deep: true }
);

watch(
  imageViewIsOpen,
  (isOpen) => {
    if (isOpen) {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
      return;
    }

    if (!interval && totalItems.value > 1) {
      interval = setInterval(() => {
        nextItemAuto();
      }, 8000);
    }
  },
  { immediate: true }
);

onMounted(() => {
  loadImageDimensions();
  viewportWidth.value = window.innerWidth;
  window.addEventListener('resize', loadImageDimensions);
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', loadImageDimensions);
  window.removeEventListener('resize', handleResize);
  if (interval) {
    clearInterval(interval);
  }
});

const handleResize = () => {
  viewportWidth.value = window.innerWidth;
};

const prevItem = (): void => {
  if (imageViewIsOpen.value) return;
  currentIndex.value = (currentIndex.value - 1 + totalItems.value) % totalItems.value; //makes sure that these are not neg
  if (interval) {
    clearInterval(interval);
    interval = setInterval(() => {
      nextItemAuto();
    }, 8000); // 8 seconds
  }
};

const nextItem = (): void => {
  if (imageViewIsOpen.value) return;
  currentIndex.value = (currentIndex.value + 1) % totalItems.value; //accounts for scrolling past last index
  if (interval) {
    clearInterval(interval);
    interval = setInterval(() => {
      nextItemAuto();
    }, 8000); // 8 seconds
  }
};

const nextItemAuto = (): void => {
  if (imageViewIsOpen.value) return;
  currentIndex.value = (currentIndex.value + 1) % totalItems.value; //accounts for scrolling past last index
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "scroller-wrapper w-full h-full flex transition-transform duration-500 ease-in-out",
      style: _normalizeStyle({ transform: `translateX(-${currentIndex.value * 100}%)` })
    }, [
      (Object.keys(displayPhotoBoxes.value).length > 0)
        ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(Object.entries(displayPhotoBoxes.value), ([boxName, images], index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: `${boxName}-${index}`,
              class: "scroller-item"
            }, [
              _createElementVNode("div", _hoisted_2, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(images, (content, contentIndex) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: `${boxName}-${contentIndex}`,
                    class: _normalizeClass(["scroller-image-div", {
                'scroller-image-div-center': isCenterImage(images, contentIndex),
                'scroller-image-div-side': isSideImage(images, contentIndex),
              }])
                  }, [
                    _createElementVNode("img", {
                      class: "cursor-pointer",
                      src: content.src,
                      style: _normalizeStyle(getImageStyle(content.src)),
                      alt: content.title || `Photo of me in ${boxName}`,
                      onClick: ($event: any) => (handleImageClick(content.src, content.title || `Photo of me in ${boxName}`, $event))
                    }, null, 12, _hoisted_3)
                  ], 2))
                }), 128))
              ])
            ]))
          }), 128))
        : _createCommentVNode("", true),
      (props.componentBoxes)
        ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(props.componentBoxes, (Component, compIndex) => {
            return (_openBlock(), _createElementBlock("div", {
              key: compIndex,
              class: "scroller-item"
            }, [
              _createElementVNode("div", _hoisted_4, [
                (_openBlock(), _createBlock(_resolveDynamicComponent(Component), { class: "scroller-component-div-item" }))
              ])
            ]))
          }), 128))
        : _createCommentVNode("", true)
    ], 4),
    _createElementVNode("div", _hoisted_5, [
      _createVNode(CardArrow, {
        class: "scroller-arrow-left",
        direction: 'left',
        onScrollLeft: prevItem
      }),
      _createVNode(CardArrow, {
        class: "scroller-arrow-right",
        direction: 'right',
        onScrollRight: nextItem
      })
    ])
  ]))
}
}

})